import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { usePermissions } from "@/hooks/usePermissions";
import { useTheme } from "@/hooks/useTheme";
import {
  Car,
  House,
  Users,
  Receipt,
  UserCircle,
  List,
  X,
  Sun,
  Moon,
  Envelope,
  Truck,
  ShoppingCart,
  Handshake,
  ChartBar,
  ChatCircle,
  CalendarBlank,
  UploadSimple
} from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const ROLE_LABELS = {
  admin: "Admin",
  commercial: "Commercial",
  preparateur: "Préparateur",
  auditeur: "Auditeur",
  comptabilite: "Comptabilité"
};

const MobileNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
    setIsOpen(false);
  };

  const navItems = [
    { path: "/dashboard", icon: House, label: "Tableau de bord", check: () => can("dashboard", "read") },
    { path: "/vehicules", icon: Car, label: "Véhicules", check: () => can("vehicles", "read") },
    { path: "/clients", icon: Users, label: "Clients", check: () => can("clients", "read") },
    { path: "/factures", icon: Receipt, label: "Factures", check: () => can("invoices", "read") },
    { path: "/factures-achat", icon: ShoppingCart, label: "Achats", check: () => can("purchase_invoices", "read") },
    { path: "/fournisseurs", icon: Truck, label: "Fournisseurs", check: () => can("fournisseurs", "read") },
    { path: "/depot-vente", icon: Handshake, label: "Depot-Vente", check: () => can("depot_vente", "read") },
    { path: "/emails", icon: Envelope, label: "Emails", check: () => can("emails", "read") },
    { path: "/rapports", icon: ChartBar, label: "Rapports", check: () => can("rapports", "read") },
    { path: "/chat", icon: ChatCircle, label: "Chat", check: () => true },
    { path: "/calendrier", icon: CalendarBlank, label: "Calendrier", check: () => true },
    { path: "/import-csv", icon: UploadSimple, label: "Import CSV", check: () => can("vehicles", "create") },
    { path: "/utilisateurs", icon: UserCircle, label: "Utilisateurs", check: () => can("users", "read") },
  ];

  const filteredNavItems = navItems.filter(item => item.check());

  return (
    <>
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white dark:bg-slate-900 border-b border-zinc-200 dark:border-slate-800 z-50 flex items-center justify-between px-4 safe-area-top">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Car size={24} weight="bold" className="text-blue-700" />
          <span className="font-black text-lg tracking-tight text-zinc-900 dark:text-slate-100">MikAuto</span>
        </Link>
        
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-slate-800 text-zinc-600 dark:text-slate-400"
            data-testid="mobile-theme-toggle"
          >
            {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-slate-800 text-zinc-700 dark:text-slate-300"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <nav 
        className={`lg:hidden fixed top-14 right-0 bottom-0 w-72 bg-white dark:bg-slate-900 z-50 transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* User info */}
        <div className="p-4 border-b border-zinc-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-zinc-900 dark:text-slate-100">{user?.name}</p>
              <p className="text-xs text-zinc-500 dark:text-slate-400">{ROLE_LABELS[user?.role]}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="p-2">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                data-testid={`mobile-nav-${item.path.slice(1)}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? "bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-400 font-semibold"
                    : "text-zinc-700 dark:text-slate-400 hover:bg-zinc-100 dark:hover:bg-slate-800"
                }`}
              >
                <Icon size={22} weight={isActive ? "fill" : "regular"} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-200 dark:border-slate-800 safe-area-bottom">
          <button
            onClick={handleLogout}
            className="w-full px-4 py-3 text-red-600 font-semibold rounded-lg hover:bg-red-50 transition-colors"
            data-testid="mobile-logout-btn"
          >
            Déconnexion
          </button>
        </div>
      </nav>

      {/* Bottom Navigation for Mobile */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-zinc-200 dark:border-slate-800 z-40 safe-area-bottom">
        <div className="flex justify-around items-center h-16">
          {filteredNavItems.slice(0, 4).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`bottom-nav-${item.path.slice(1)}`}
                className={`flex flex-col items-center justify-center px-3 py-2 ${
                  isActive ? "text-blue-700 dark:text-blue-400" : "text-zinc-500 dark:text-slate-500"
                }`}
              >
                <Icon size={22} weight={isActive ? "fill" : "regular"} />
                <span className="text-xs mt-1 font-medium">{item.label.split(' ')[0]}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default MobileNav;
